import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Box,
  Skeleton,
  Button,
  Badge,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useMyTasks, useInboxCount } from '@/hooks/taskmanagement/useTaskInbox';

const PRIORITY_COLORS: Record<string, 'error' | 'warning' | 'info' | 'default'> = {
  URGENT: 'error',
  HIGH: 'warning',
  NORMAL: 'info',
  LOW: 'default',
};

function formatRelativeDue(dueAt: string | null | undefined): string | null {
  if (!dueAt) return null;
  const due = new Date(dueAt);
  const now = new Date();
  const diffMs = due.getTime() - now.getTime();
  const absDiffMs = Math.abs(diffMs);
  const isPast = diffMs < 0;

  const minutes = Math.floor(absDiffMs / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  let label: string;
  if (days > 0) {
    label = `${days}d`;
  } else if (hours > 0) {
    label = `${hours}h`;
  } else {
    label = `${minutes}m`;
  }

  return isPast ? `${label} overdue` : `in ${label}`;
}

interface TaskInboxWidgetProps {
  limit?: number;
}

export function TaskInboxWidget({ limit = 5 }: TaskInboxWidgetProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, isLoading } = useMyTasks({
    maxResultCount: limit,
    sorting: 'priority desc, dueAt asc',
  });
  const countQuery = useInboxCount();

  const tasks = useMemo(() => data?.items ?? [], [data]);
  const totalCount = countQuery.data?.myTasksCount ?? 0;

  if (isLoading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Skeleton width="40%" height={24} />
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} height={48} sx={{ mt: 1 }} />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2, '&:last-child': { pb: 1 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Badge badgeContent={totalCount} color="primary" max={99}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, pr: 1 }}>
              {t('dashboard.myTasks', 'My Tasks')}
            </Typography>
          </Badge>
        </Box>

        {tasks.length === 0 ? (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 40, color: 'success.main', opacity: 0.6 }} />
            <Typography variant="body2" color="text.secondary">
              {t('dashboard.noTasks', 'No pending tasks')}
            </Typography>
          </Box>
        ) : (
          <>
            <List disablePadding sx={{ flex: 1, overflow: 'auto' }}>
              {tasks.map((task) => {
                const relDue = formatRelativeDue(task.dueAt);
                return (
                  <ListItemButton
                    key={task.id}
                    dense
                    onClick={() => navigate(`/taskmanagement/inbox/${task.id}`)}
                    sx={{ px: 1, py: 0.5, borderRadius: 1 }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body2" noWrap>
                          {task.title}
                        </Typography>
                      }
                      slotProps={{ secondary: { component: 'div' } }}
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                          <Chip
                            label={task.priority}
                            size="small"
                            color={PRIORITY_COLORS[task.priority] ?? 'default'}
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                          {relDue && (
                            <Typography
                              variant="caption"
                              color={task.isOverdue ? 'error.main' : 'text.secondary'}
                              sx={{ fontWeight: task.isOverdue ? 600 : 400 }}
                            >
                              {relDue}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItemButton>
                );
              })}
            </List>
            {totalCount > limit && (
              <Button
                size="small"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/taskmanagement/inbox')}
                sx={{ alignSelf: 'flex-end', mt: 0.5, textTransform: 'none' }}
              >
                {t('dashboard.viewAllTasks', 'View All Tasks')}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
